import moment, { Moment } from '@e3dc-react/shell/Libs/moment'
import { PowermeterHistory } from '../../../Types/responses/powermeter-values/{sn}'
import { WallboxHistory } from '../../../Types/responses/wallboxes/{sn}'
import { getStore } from '../state/store'
import { Breakthrough, ForecastPower, HistoryData } from '../models/systemDataStoreModel'
import { binarySearchObject } from './binarySearch'
import { getFetchRanges, TimeRange } from './datetime'
import { ChartView } from '../../chart-kit/shared/utils/chartModel'

export const ONE_MINUTE_MS = 60000
export const FIFTEEN_MINUTES_MS = 900000
export const THIRTY_MINUTES_MS = 1800000
export const FOURTY_FIVE_MINUTES_MS = 2700000
export const ONE_HOUR_MS = 3600000
export const ONE_DAY_MS = 86400000
export const TEN_DAYS_MS = 864000000
export const ONE_HOUR_S = 3600

export const COUNTER_STATUS_KEYS = [
  'BatPowerIn',
  'BatPowerOut',
  'NetIn',
  'NetOut',
  'Consumption',
  'DC_PowerL1',
  'DC_PowerL2',
  'DC_PowerL3',
  'energyAll',
  'energySolar',
  'energyDischarged',
  'NetIn',
  'NetOut',
]

export function correctTimestamp(ts: number): [number, number] {
  // 15 minutes = 900000 = 15*60*1000
  // 7.5 minutes = 450000 = 7.5*60*1000
  // Zeitstempel sind nicht immer in 15min schritten, falls nicht: abändern
  const mod = ts % 900000

  // no fix
  if (mod === 0) return [0, ts]
  // round up
  if (mod >= 450000) return [1, ts - mod + 900000]
  // round down
  return [-1, ts - mod]
}

/**
 * convert ASOC to Battery Capacity
 */
export const asocToBatCapa = (ASOC: number, batteryCapacity: number | undefined): number => {
  if (!batteryCapacity) return 0
  return (batteryCapacity / 100) * ASOC
}

/**
 * convert Battery Capacity to ASOC
 */
export const batteryCapacityToAsoc = (batteryLevel = 0, batteryCapacity = 0): number => {
  return (100 / batteryCapacity) * batteryLevel
}

export const getHistoryDataFromStore = (from: Moment, to: Moment, serialNo: string, view?: ChartView): HistoryData[] => {
  const store = getStore()
  if (!store) return []

  const storeData: HistoryData[] = []
  const monthsInRange = getFetchRanges(from, to).map((next: TimeRange) => next.from)
  /** To get the correct difference for the first history value for week, we need the values one hour before */
  const beforeTime = view === 'week' ? 4 * FIFTEEN_MINUTES_MS : FIFTEEN_MINUTES_MS
  const beforeFrom = from.valueOf() - beforeTime // moment(from).subtract(15, 'minutes')
  const monthBefore = moment(beforeFrom).startOf('month').valueOf()

  const prevData = binarySearchObject(store.getState().historyData[serialNo]?.[monthBefore]?.data, beforeFrom, 'time')

  monthsInRange.forEach(month => {
    const dataFromStore = store.getState().historyData[serialNo]?.[month]?.data

    if (dataFromStore) storeData.push(...dataFromStore)
  })

  const fromNumber = from.valueOf()
  const toNumber = to.valueOf()
  const filteredStoreData = storeData.filter(data => data?.time >= fromNumber && data?.time <= toNumber)

  if (prevData) {
    return [prevData, ...filteredStoreData]
  }

  return filteredStoreData
}

export const getWallboxHistoryDataFromStore = (from: Moment, to: Moment, serialNo: string): Record<string, WallboxHistory[]> => {
  const monthsInRange = getFetchRanges(from, to).map((next: TimeRange) => next.from)
  const storeData: Record<string, WallboxHistory[]> = {}
  monthsInRange.forEach(month => {
    const dataFromStore = getStore()?.getState().historyData[serialNo]?.[month]?.wallboxes

    if (!dataFromStore) return

    Object.keys(dataFromStore).forEach(id => {
      if (storeData[id] === undefined) storeData[id] = []
    })

    Object.keys(dataFromStore).forEach(id => {
      storeData[id].push(...dataFromStore[id])
    })
  })

  return storeData
}

export const getPowermeterHistoryDataFromStore = (from: Moment, to: Moment, serialNo: string): Record<string, PowermeterHistory[]> => {
  const monthsInRange = getFetchRanges(from, to).map((next: TimeRange) => next.from)
  const storeData: Record<string, PowermeterHistory[]> = {}
  monthsInRange.forEach(month => {
    const dataFromStore = getStore()?.getState().historyData[serialNo]?.[month]?.powermeters

    if (!dataFromStore) return

    Object.keys(dataFromStore).forEach(id => {
      if (storeData[id] === undefined) storeData[id] = []
    })

    Object.keys(dataFromStore).forEach(id => {
      storeData[id].push(...dataFromStore[id])
    })
  })

  return storeData
}

export const getBreakthroughDataFromStore = (from: Moment, to: Moment, serialNo: string): Breakthrough[] => {
  const storeData: Breakthrough[] = []
  const monthsInRange = getFetchRanges(from, to).map((next: TimeRange) => next.from)

  monthsInRange.forEach(month => {
    const dataFromStore = getStore()?.getState().breakthrough[serialNo]?.[month]

    if (dataFromStore) storeData.push(...dataFromStore)
  })

  return storeData
}

export const getForecastPowerDataFromStore = (
  from: Moment,
  to: Moment,
  serialNo: string,
  resolution: 'quarter' | 'hour' | 'day' = 'quarter'
): ForecastPower[] => {
  const storeData: ForecastPower[] = []
  const monthsInRange = getFetchRanges(from, to).map((next: TimeRange) => next.from)

  monthsInRange.forEach(month => {
    const dataFromStore = getStore()?.getState().forecastPower[serialNo]?.[month]

    if (dataFromStore) storeData.push(...dataFromStore)
  })

  const fromNumber = moment(from).valueOf()
  const toNumber = moment(to).valueOf()

  const filteredData = storeData.filter(forecastPower => forecastPower?.time >= fromNumber && forecastPower?.time <= toNumber)

  if (resolution === 'hour') {
    return filteredData.filter(fp => moment(fp.time).minute() === 0)
  }
  if (resolution === 'day') {
    return filteredData.filter(fp => moment(fp.time).hour() === 0 && moment(fp.time).minute() === 0)
  }

  /* TODO: optimize the filter - find the first and last item with binary search and splice the array */
  return filteredData
}

export const getRoundedNumber = (n: number): number => {
  if (!n) return 0

  return Number(n.toFixed(2))
}

/**
 * Performs a binary search on an array of objects to find the cutoff index where the value of the specified key is greater than or equal to the cutoff.
 * @template T - The type of objects in the array.
 * @param {T[]} arr - The (sorted) array to search.
 * @param {keyof T} key - The key of the numeric property to compare.
 * @param {number} cutoff - The cutoff value to search for.
 * @returns {number} The index of the first element whose key value is greater than or equal to the cutoff.
 * @throws {Error} If the specified key does not correspond to a number in the array elements.
 */
export const getBinarySearchCutoffIndex = <T>(arr: T[], key: keyof T, cutoff: number): number => {
  let left = 0
  let right = arr.length

  while (left < right) {
    const mid = Math.floor((left + right) / 2)
    const value = arr[mid][key]

    if (typeof value !== 'number') {
      throw new Error(`Field '${String(key)}' must be a number`)
    }

    if (value < cutoff) {
      left = mid + 1
    } else {
      right = mid
    }
  }

  return left
}
